import React, { useCallback, useMemo, useRef } from 'react';

import { Box, HStack, Icon, Skeleton, VStack } from '@rbilabs/universal-components';
import { randomUUID as uuidv4 } from 'expo-crypto';
import { useIntl } from 'react-intl';

import { CartDebugMenu } from 'components/debug-menu/cart-debug';
import FavoriteEntryButton from 'components/favorite-entry-button';
import Picture from 'components/picture';
import { useCart } from 'hooks/use-cart';
import { useCartItemEditingTools } from 'hooks/use-cart-item-editing-tools';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import useFoodware from 'hooks/use-foodware';
import { useIsEditCartEnabled } from 'hooks/use-is-edit-cart-enabled';
import { useIsExtraSmallBreakpoint } from 'hooks/use-media-query';
import { ClickEventComponentNames, CustomEventNames, logButtonClick } from 'state/amplitude';
import { DISCOUNT_OFFER_CART_ID, useCartContext } from 'state/cart';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useGetAvailableRewards } from 'state/loyalty/hooks/use-get-available-rewards';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { OrderStatus } from 'state/order/types';
import { primitive } from 'styles/constants/primitives';
import { getMenuObjectCartQuantity } from 'utils/cart';
import { isCartEntryItem } from 'utils/cart/is-cart-type';
import { HapticsNotificationType, hapticNotification } from 'utils/haptic';
import { isHydratedReward } from 'utils/minimal-cart';

import { CartItemOffer } from './cart-item-offer';
import { CartItemPriceAndCalories } from './cart-item-price-and-calories';
import { Price } from './price';
import { RedeemReward } from './redeem-reward';
import {
  CartItemButton,
  CartItemButtonText,
  CartItemDescription,
  CartItemEditButtonContainer,
  CartItemEditContainer,
  CartItemInfoContainer,
  CartItemName,
  CartItemNameHeaderContainer,
  Item,
} from './styled';
import { ICartItemProps } from './types';
import { ComposedCartItemType, useComposeCartItemUIData } from './use-compose-cart-item-ui-data';
import { useRedeemReward } from './use-redeem-reward-state';

export const CartItemExperiment: React.FC<React.PropsWithChildren<ICartItemProps>> = React.memo(
  ({ item, readOnly = false, rewardApplied = false, selectedOfferId, onAnnounceA11yMessage }) => {
    const hasSelectedOffer = !!selectedOfferId;

    const isExtraSmallScreen = useIsExtraSmallBreakpoint();
    const isEditOfferButtonEnabled = useFlag(LaunchDarklyFlag.ENABLE_EDIT_OFFERS_CART_BUTTON);
    const isDebugMenuEnabled = useFlag(LaunchDarklyFlag.ENABLE_DEBUG_TOOL);
    const isUnifiedMenuEnabled = useEnableUnifiedMenu();

    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    const offerSkeletonRenderedRef = useRef(false);

    // TODO: check if we need to evaluate LoyaltyOffer as cartHasIncentive
    const isEditCartEnabled = useIsEditCartEnabled({
      cartHasIncentive: hasSelectedOffer || rewardApplied,
    });
    const { handleEditItem, handleRemoveItem } = useCartItemEditingTools({
      item,
      isOffer: hasSelectedOffer,
    });

    const composedItem = useComposeCartItemUIData(item);

    const itemKey = useMemo(() => item.cartId || item._id, [item._id, item.cartId]);
    const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
    const appliedLoyaltyRewardsArray = useAppSelector(
      selectors.loyalty.selectAppliedLoyaltyRewardsArray
    );
    const loyaltyEnabled = useIsLoyaltyEnabled();
    const { getAvailableRewardFromCartEntry } = useGetAvailableRewards();
    const { addItemToCart, calculateCartTotalWithDiscount, cartEntries } = useCart();
    const evaluatingIncentives = useAppSelector(selectors.loyalty.selectOffersLoading);
    const { orderStatus, serverOrder, shouldSkipPriceOrder } = useCartContext();
    const { maxCartQuantityMet } = getMenuObjectCartQuantity({ menuObject: item, cartEntries });

    const { isCartTotalNegative = false } = calculateCartTotalWithDiscount
      ? calculateCartTotalWithDiscount()
      : {};

    const loyaltyRewardAvailable = getAvailableRewardFromCartEntry(item);
    const { isFoodware } = useFoodware();
    const { hasSufficientBalance, limitPerOrderMet } = useRedeemReward(item);

    const handleDuplicateItem = useCallback(() => {
      logButtonClick(
        {
          attributes: {
            component: ClickEventComponentNames.BUTTON,
            name: CustomEventNames.BUTTON_CLICK_DUPLICATE_ITEM,
          },
        },
        { logDuplicateClickEvent: true }
      );

      hapticNotification({ type: HapticsNotificationType.SUCCESS });
      const newCartId = uuidv4();
      const canRedeemReward = hasSufficientBalance && !limitPerOrderMet;
      const currentCartEntryHasRewardApplied = appliedLoyaltyRewardsArray?.some(
        reward => reward.cartId === item.cartId
      );

      addItemToCart({ ...item, cartId: newCartId });

      onAnnounceA11yMessage(
        `${formatMessage({ id: 'addToCartSuccess' }, { itemName: item.name })}`
      );

      if (
        currentCartEntryHasRewardApplied &&
        canRedeemReward &&
        loyaltyRewardAvailable?.rewardBenefitId
      ) {
        dispatch(
          actions.loyalty.applyReward({
            rewardBenefitId: loyaltyRewardAvailable.rewardBenefitId,
            cartId: newCartId,
            quantity: 1,
          })
        );
      }
    }, [
      addItemToCart,
      item,
      hasSufficientBalance,
      limitPerOrderMet,
      appliedLoyaltyRewardsArray,
      loyaltyRewardAvailable?.rewardBenefitId,
      dispatch,
      formatMessage,
      onAnnounceA11yMessage,
    ]);

    const loyaltyOffer = appliedOffers.find(lylOffer => lylOffer.cartId === item.cartId);
    const isSingleItem = isCartEntryItem(item) || (isUnifiedMenuEnabled && isHydratedReward(item));
    const itemHasHeader = !isSingleItem || loyaltyOffer?.id;
    const isPriceRequested = !serverOrder || orderStatus === OrderStatus.PRICE_REQUESTED;
    const isOrderPriceLoading = !isCartTotalNegative && isPriceRequested && !shouldSkipPriceOrder;
    const isLoading = isOrderPriceLoading || evaluatingIncentives;
    const showSkeleton = !offerSkeletonRenderedRef.current && isLoading;
    const isExtra = item.isExtra;
    const isItemEditableInline = !hasSelectedOffer && !isExtra && !loyaltyOffer;
    const hasOffer = (hasSelectedOffer && selectedOfferId === item.sanityId) || loyaltyOffer;
    const showFavorite = !hasOffer && !isExtra;
    const showRedeemRewardButton = loyaltyEnabled && loyaltyRewardAvailable && isItemEditableInline;

    const onRemoveItemPress = useCallback(() => {
      hapticNotification({ type: HapticsNotificationType.WARNING });
      handleRemoveItem();
      logButtonClick(
        {
          attributes: {
            component: ClickEventComponentNames.BUTTON,
            name: CustomEventNames.BUTTON_CLICK_CART_ITEM_REMOVED,
          },
        },
        { logDuplicateClickEvent: true }
      );
    }, [handleRemoveItem]);

    const ItemDescription = useMemo(() => {
      return ({ composedCartItem }: { composedCartItem: ComposedCartItemType }) => {
        const { name, image } = composedCartItem;

        return (
          <CartItemDescription>
            <VStack paddingRight="$4">
              <Picture size="$14" alt={name} image={image} />
            </VStack>
            <VStack flex={1} justifyContent="center">
              <CartItemName {...(!itemHasHeader && { marginRight: '$4' })} testID="cart-item-label">
                {name}
              </CartItemName>
              <CartItemPriceAndCalories cartItem={composedCartItem} isSingleItem={isSingleItem} />
            </VStack>
          </CartItemDescription>
        );
      };
    }, [isSingleItem, itemHasHeader]);

    const duplicateButton = useMemo(
      () => (
        <CartItemButton
          onPress={handleDuplicateItem}
          testID="duplicate-button"
          keepIconSize
          accessibilityLabel={`${formatMessage({ id: 'duplicate' })} ${item.name} `}
          leftIcon={<Icon name="custom:DuplicateItem" size="md" />}
          marginRight="$4"
          disabled={maxCartQuantityMet}
          color={maxCartQuantityMet ? primitive.$blackOpacity50 : primitive.bk.$bbqBrown}
        />
      ),
      [handleDuplicateItem, maxCartQuantityMet, formatMessage, item.name]
    );

    // Render the header for offers or non single items.
    const header = useMemo(() => {
      if (loyaltyOffer?.id) {
        return (
          <CartItemOffer
            isLoading={showSkeleton}
            loyaltyOfferId={loyaltyOffer.id}
            cmsOfferId={loyaltyOffer.cmsId}
            skeletonRenderedRef={offerSkeletonRenderedRef}
          />
        );
      }

      return (
        !isSingleItem && (
          <CartItemNameHeaderContainer>
            <CartItemName testID="cart-item-label">{item.name}</CartItemName>
          </CartItemNameHeaderContainer>
        )
      );
    }, [loyaltyOffer?.id, loyaltyOffer?.cmsId, isSingleItem, item.name, showSkeleton]);

    if (item.isDonation || isFoodware(item)) {
      return null;
    }

    /**
     * The whole order discounts should not show in the list of cart items.
     * The details for this is explicitly shown outside of the cart item list.
     * The offer must be applied to the cart items, so we're simply removing it
     * visually from the list here.
     */
    if (loyaltyOffer?.cartId === DISCOUNT_OFFER_CART_ID) {
      return null;
    }

    const favoriteAbsoluteButton = showFavorite && (
      <Box position="absolute" right={0} zIndex={1}>
        <FavoriteEntryButton cartEntry={item} />
      </Box>
    );

    if (!composedItem) {
      return null;
    }

    return (
      <Item testID={`cart-item-${itemKey}`}>
        {favoriteAbsoluteButton}
        {itemHasHeader && header}
        <CartItemInfoContainer>
          {isDebugMenuEnabled && <CartDebugMenu cartEntry={item} />}
          {composedItem.children?.length ? (
            composedItem.children.map((composedCartItem, key) => {
              return <ItemDescription key={key} composedCartItem={composedCartItem} />;
            })
          ) : (
            <ItemDescription composedCartItem={composedItem} />
          )}
        </CartItemInfoContainer>

        {!readOnly && (
          <>
            <CartItemEditContainer>
              <CartItemEditButtonContainer marginRight="$4">
                {hasOffer && showSkeleton ? (
                  <Skeleton height="$7" rounded="8" width={'$17.5'} />
                ) : (
                  <>
                    <CartItemButton
                      keepIconSize
                      accessibilityLabel={`${formatMessage({ id: 'remove' })} ${item.name} `}
                      testID="remove-button"
                      onPress={onRemoveItemPress}
                      leftIcon={<Icon variant="bkRemove" size="md" />}
                      marginRight="$4"
                    />
                    {!loyaltyOffer?.id && !isExtra && duplicateButton}
                    {isEditCartEnabled && (loyaltyOffer ? isEditOfferButtonEnabled : true) && (
                      <CartItemButton
                        accessibilityLabel={`${formatMessage({ id: 'edit' })} ${item.name} `}
                        testID="edit-button"
                        onPress={handleEditItem}
                      >
                        <CartItemButtonText>{formatMessage({ id: 'edit' })}</CartItemButtonText>
                      </CartItemButton>
                    )}
                  </>
                )}
              </CartItemEditButtonContainer>
              {hasOffer && showSkeleton ? (
                <Skeleton height="$7" rounded="12" width="$20" />
              ) : (
                <CartItemEditButtonContainer justifyContent="flex-end">
                  {showRedeemRewardButton && (
                    <RedeemReward
                      isRewardApplied={rewardApplied}
                      item={item}
                      reward={loyaltyRewardAvailable}
                    />
                  )}
                  {(!showRedeemRewardButton || (showRedeemRewardButton && !isExtraSmallScreen)) && (
                    <HStack marginLeft="$2">
                      <Price item={item} isOffer={hasSelectedOffer} rewardApplied={rewardApplied} />
                    </HStack>
                  )}
                </CartItemEditButtonContainer>
              )}
            </CartItemEditContainer>
            {showRedeemRewardButton && isExtraSmallScreen && (
              <HStack paddingX="$4" paddingY={'$2'} justifyContent="flex-end">
                <Price item={item} isOffer={hasSelectedOffer} rewardApplied={rewardApplied} />
              </HStack>
            )}
          </>
        )}
      </Item>
    );
  }
);
